import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { advanced } from '@joey-wallet/wc-client/react';
import { joeyConfig } from "./joeyConfig";
import core from '@joey-wallet/wc-client/core';
const { Provider: JoeyProvider, useProvider } = advanced.default;
const wallets = core.constants.wallets;
/**
 * Internal Component to bridge Joey's SDK useProvider with our Custom Context
 * This mimics the structure of your XRPLWalletProvider.js
 */


export const JoeyWalletContext = createContext(null);



function JoeyInternalProvider({ children }) {
  const { actions, session: sdkSession, accounts, provider, chain } = useProvider();
  const [address, setAddress] = useState(null);
  const [session, setSession] = useState(null); // Local session state to match XRPL pattern

  /* // Sync address when accounts change
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      // Extract classic address: 'xrpl:0:rAddress...' -> 'rAddress'
      const classicAddress = accounts[0].split(":")[2];
      setAddress(classicAddress);
    } else {
      setAddress(null);
    }
  }, [accounts]); */

  // Helper to extract classic address: 'xrpl:0:rAddress...' -> 'rAddress'
  const extractAddress = (sessionObj) => {
    try {
      const account = sessionObj?.namespaces?.xrpl?.accounts[0];
      return account ? account.split(":")[2] : null;
    } catch (e) {
      return null;
    }
  };

  // Sync internal states with the SDK's reactive state
  useEffect(() => {
    if (sdkSession) {
      setSession(sdkSession);
      setAddress(extractAddress(sdkSession));
    } else {
      setSession(null);
      setAddress(null);
    }
  }, [sdkSession, accounts]);

  // Sync the local session state with the Joey SDK session state
  useEffect(() => {
    setSession(sdkSession);
  }, [sdkSession]);

const connect = useCallback(async () => {
    try {
      if (sdkSession) return sdkSession;

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const resp = await actions.generate({
          walletId: wallets.joey.projectId,
          openModal: false
        });
        const deeplink = resp?.data?.deeplink
        const uri  = resp?.data?.uri;
      // On Mobile: Use generate to get the targeted deep link immediately
      if (isMobile) {
        if (resp.error) throw resp.error;
        if (deeplink) {
          window.location.href = deeplink;
        }

        // Return a promise that resolves when the session is actually established
        // The UI in Login.js will wait for this or use the useEffect sync
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            if (provider?.off) provider.off('connect', onConnect);
            reject(new Error("Connection timed out: Please try again."));
          }, 60000); // 60 second timeout for mobile hand-off

          const onConnect = (newSession) => {
            clearTimeout(timeout);
            if (provider?.off) provider.off('connect', onConnect);
            resolve(newSession);
          };

          if (provider?.on) {
            provider.on('connect', onConnect);
          }
        });
      } 
      
      // On Desktop: Use the standard connect flow which is more stable for modals
      const response = await actions.connect();
      if (response.error) throw response.error;

      setSession(response.data);
      setAddress(extractAddress(response.data));
      return response.data;

    } catch (err) {
      console.error("Joey Connection failed:", err);
      throw err;
    }
  }, [actions, provider, sdkSession]);

  const reconnect = useCallback(async (targetSession) => {
    try {
      const sess = targetSession || session;
      if (!sess) throw new Error("No session available to reconnect");
      
      const response = await actions.reconnect(sess);
      if (response.error) throw response.error;
      
      return response;
    } catch (err) {
      console.error("Joey Reconnect failed:", err);
      throw err;
    }
  }, [actions, session]);

  const disconnect = useCallback(async () => {
    try {
      await actions.disconnect();
    } catch (err) {
      console.warn("Joey Disconnect error:", err);
    } finally {
      // Clear local states on disconnect to mirror XRPL behavior
      setSession(null);
      setAddress(null);
    }
  }, [actions]);

  // Expose the EXACT same property names as XRPLWalletProvider
  const value = useMemo(() => ({
    connect,
    disconnect,
    connected: !!session,
    address,
    account: address, 
    setAccount: setAddress,
    session,
    setSession,       // Added to match XRPLWalletProvider parity if used manually
    client: actions,  // Aliased actions to client so it acts like signClient
    generate: async () => await actions.generate({ 
        walletId: wallets.joey.projectId,
    }),
    provider,
    chain,
    reconnect
  }), [connect, disconnect, session, address, actions]);

  return (
    <JoeyWalletContext.Provider value={value}>
      {children}
    </JoeyWalletContext.Provider>
  );
}

// 3. The Exported Provider
export function JoeyWalletProvider({ children }) {
  return (
    <JoeyProvider config={joeyConfig}>
      <JoeyInternalProvider>
        {children}
      </JoeyInternalProvider>
    </JoeyProvider>
  );
}

// 4. The Exported Hook
export function useJoeyWallet() {
  const ctx = useContext(JoeyWalletContext);
  if (!ctx) throw new Error("useJoeyWallet must be used inside JoeyWalletProvider");
  return ctx;
}